import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { DatePipe } from '@angular/common';

import { IconComponent } from '../../design-system/components/icon/icon.component';

import { MedicalRecordsPdfInput } from '../../medical-records/medical-records.models';

import { MedicalRecordsPdfService } from '../../medical-records/medical-records.service';
import { VaccinesPdfService } from '../../vaccines/vaccines.service';
import { VaccinesPdfInput } from '../../vaccines/vaccines.models';

@Component({
  selector: 'lib-documents',
  standalone: true,
  imports: [DatePipe, IconComponent],
  templateUrl: './documents.component.html',
  styleUrl: './documents.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MedicalRecordsPdfService, VaccinesPdfService],
})
export class DocumentsComponent {
  private readonly medicalRecordsPdfService = inject(MedicalRecordsPdfService);

  private readonly vaccinesPdfService = inject(VaccinesPdfService);

  readonly medicalRecordsPdfData = input<MedicalRecordsPdfInput | null>(null);

  readonly medicalRecordPdfUrl = input<string | null>(null);

  readonly vaccinesPdfData = input<VaccinesPdfInput | null>(null);

  readonly medicalRecordsLastUpdated = input<Date | string | null>(null);

  readonly vaccineRecordsLastUpdated = input<Date | string | null>(null);

  readonly adoptionAgreementUrl = input<string | null>(null);

  readonly adoptionAgreementLastUpdated = input<Date | string | null>(null);

  readonly generationError = output<unknown>();

  readonly hasMedicalRecords = computed(() =>
    Boolean(
      this.normalizedMedicalRecordPdfUrl() ||
      this.medicalRecordsPdfData()?.records.length,
    ),
  );

  readonly normalizedMedicalRecordPdfUrl = computed(() => {
    const url = this.medicalRecordPdfUrl()?.trim();

    return url || null;
  });

  readonly hasVaccineRecords = computed(() =>
    Boolean(this.vaccinesPdfData()?.vaccines.length),
  );

  readonly normalizedAdoptionAgreementUrl = computed(() => {
    const url = this.adoptionAgreementUrl()?.trim();

    return url || null;
  });

  readonly hasAdoptionAgreement = computed(() =>
    Boolean(this.normalizedAdoptionAgreementUrl()),
  );

  readonly hasAnyDocuments = computed(
    () =>
      this.hasMedicalRecords() ||
      this.hasVaccineRecords() ||
      this.hasAdoptionAgreement(),
  );

  generateMedicalRecordsPdf(): void {
    const pdfData = this.medicalRecordsPdfData();

    if (!pdfData?.records.length) {
      return;
    }

    try {
      this.medicalRecordsPdfService.download(pdfData);
    } catch (error: unknown) {
      this.generationError.emit(error);
    }
  }

  generateVaccineRecordsPdf(): void {
    const pdfData = this.vaccinesPdfData();

    if (!pdfData?.vaccines.length) {
      return;
    }

    try {
      const pdf = this.vaccinesPdfService.build(pdfData);

      this.vaccinesPdfService.downloadDocument(
        pdf.documentDefinition,
        pdf.filename,
      );
    } catch (error: unknown) {
      this.generationError.emit(error);
    }
  }
}
